import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { supplierProductsApi } from "../../../api/inventory/supplier-products";

export function SupplierProductsOverview({ supplierId }) {
  const { t } = useTranslation();
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  const roles = useSelector((state) => state.session.currentUser?.roles || []);
  const canViewCost = roles.some((role) => role.name === "admin" || role.name === "inventory_finance");
  const [query, setQuery] = useState("");
  const [page, setPage] = useState(1);
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => { setPage(1); }, [query, supplierId, companyId]);
  useEffect(() => {
    setRows([]);
    setTotal(0);
    if (!supplierId || !companyId) return undefined;
    let active = true;
    setLoading(true);
    setError(false);
    supplierProductsApi.list({ supplier_id: supplierId, query, page, per_page: 50 })
      .then((response) => {
        if (!active) return;
        setRows(response.data.data || []);
        setTotal(response.data.total_count || 0);
      }).catch(() => { if (active) setError(true); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [supplierId, companyId, query, page]);

  return <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
    <div className="flex flex-wrap items-center justify-between gap-4 border-b border-slate-200 px-5 py-4">
      <div><h2 className="text-lg font-semibold text-slate-900">{t("supplierProducts.title")}</h2>
        <p className="mt-1 text-sm text-slate-500">{t("supplierProducts.supplierDescription")}</p></div>
      <input type="search" value={query} onChange={(event) => setQuery(event.target.value)}
        placeholder={t("supplierProducts.search")} aria-label={t("supplierProducts.search")}
        className="min-h-[42px] w-full rounded-xl border border-slate-300 px-3 text-sm sm:w-72" />
    </div>
    {error && <p role="alert" className="m-5 rounded-xl bg-amber-50 p-3 text-sm text-amber-900">{t("supplierProducts.loadFailed")}</p>}
    {loading ? <p className="px-5 py-6 text-sm text-slate-500">{t("Loading...")}</p> : rows.length === 0 && !error ?
      <p className="px-5 py-8 text-center text-sm text-slate-500">{t("supplierProducts.emptySupplier")}</p> :
      <div className="overflow-x-auto"><table className="w-full min-w-[570px] text-left text-sm">
        <thead className="bg-slate-50 text-xs font-semibold text-slate-500"><tr>
          <th className="px-5 py-3">{t("supplierProducts.itemNumber")}</th>
          <th className="px-5 py-3">{t("supplierProducts.productTitle")}</th>
          <th className="px-5 py-3">{t("supplierProducts.salesProduct")}</th>
          {canViewCost && <th className="px-5 py-3 text-right">{t("supplierProducts.purchasePrice")}</th>}
        </tr></thead>
        <tbody className="divide-y divide-slate-100">{rows.map((row) => <tr key={row.id}>
          <td className="px-5 py-3 font-medium text-slate-800">{row.item_number}</td>
          <td className="px-5 py-3">{row.title}</td>
          <td className="px-5 py-3"><Link to={`/dashboard/products/${row.product_id}`} className="font-semibold text-[#1463C7] hover:underline">{row.sales_product_title}</Link></td>
          {canViewCost && <td className="px-5 py-3 text-right tabular-nums">{row.purchase_price ?? "—"}</td>}
        </tr>)}</tbody>
      </table></div>}
    {total > 50 && <div className="flex items-center justify-end gap-3 border-t border-slate-100 px-5 py-3 text-sm">
      <button type="button" disabled={page === 1} onClick={() => setPage((current) => current - 1)} className="rounded-lg border px-3 py-1.5 disabled:opacity-40">{t("Previous")}</button>
      <span>{page} / {Math.ceil(total / 50)}</span>
      <button type="button" disabled={page * 50 >= total} onClick={() => setPage((current) => current + 1)} className="rounded-lg border px-3 py-1.5 disabled:opacity-40">{t("Next")}</button>
    </div>}
  </section>;
}
